import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authApi } from '../../api/auth.api';
import { emailLocale } from '../../i18n';
import { useAuthStore } from '../../stores/auth.store';
import { toast } from '../ui/Toast';
import Spinner from '../ui/Spinner';
import { Icon, Mail, X } from '../ui/Icon';

export default function UnverifiedBanner() {
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const [sending, setSending] = useState(false);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem('unverified-banner-dismissed') === '1');

  if (!user || user.emailVerifiedAt || dismissed) return null;

  async function handleResend() {
    setSending(true);
    try {
      await authApi.resendVerification(emailLocale());
      toast(t('auth.unverified.resent'), 'success');
    } catch (err: unknown) {
      const code = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      if (code === 'ALREADY_VERIFIED') {
        toast(t('auth.unverified.alreadyVerified'), 'info');
      } else {
        toast(t('auth.unverified.resendFailed'), 'error');
      }
    } finally {
      setSending(false);
    }
  }

  function handleDismiss() {
    sessionStorage.setItem('unverified-banner-dismissed', '1');
    setDismissed(true);
  }

  return (
    <div className="relative flex items-center gap-3 px-4 md:px-6 py-2.5 text-sm border-b" style={{ background: '#fef9ef', borderColor: '#f0d8a8', color: '#5c3a0a' }}>
      <div className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center" style={{ background: '#d9b382', color: '#5c3a0a' }}>
        <Icon as={Mail} size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] leading-tight">
          <strong className="font-semibold">{t('auth.unverified.title')}</strong>
          <span className="mx-1.5 opacity-60">·</span>
          <span>{t('auth.unverified.body', { email: user.email })}</span>
        </p>
      </div>
      <button
        onClick={handleResend}
        disabled={sending}
        className="shrink-0 font-mono text-[11px] tracking-wide uppercase font-semibold px-3 py-1.5 rounded-lg transition-colors cursor-pointer disabled:opacity-50"
        style={{ background: '#5c3a0a', color: '#fef9ef' }}
      >
        {sending ? <Spinner size="sm" /> : t('auth.unverified.resend')}
      </button>
      <button
        onClick={handleDismiss}
        className="shrink-0 w-6 h-6 rounded-md flex items-center justify-center hover:bg-black/10 transition-colors cursor-pointer"
        aria-label={t('common.close')}
        style={{ color: '#5c3a0a' }}
      >
        <Icon as={X} size={14} />
      </button>
    </div>
  );
}
